import type { Company } from '@pn/core/domain/intel/company';
import { findOrThrow } from '@pn/core/utils/logic';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isArray, uniq, uniqBy } from 'lodash-es';

export type CompaniesStorageState = {
  isFetching: boolean;
  isError: boolean;
  companies: Company[];
  checkedCompanyIds: Company['id'][];
  checkedCompaniesColors: Record<Company['id'], string>;
  selectedCompanyId: Company['id'] | undefined;
  companyNameToIdRecord: Record<Company['name'], Company['id']>;
  recentlyViewedCompanyIds: Company['id'][];
};

const initialState: CompaniesStorageState = {
  isFetching: false,
  isError: false,
  companies: [],
  checkedCompanyIds: [],
  checkedCompaniesColors: {},
  selectedCompanyId: undefined,
  companyNameToIdRecord: {},
  recentlyViewedCompanyIds: [],
};

const createCompaniesSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state) => {
        state.isFetching = true;
        state.isError = false;
      },
      receive: (state, { payload }: PayloadAction<Company[]>) => {
        state.companies = payload;
        state.isFetching = false;
        state.companyNameToIdRecord = payload.reduce(
          (acc, company) => ({
            ...acc,
            [company.name]: company.id,
          }),
          {}
        );
      },
      error: (state) => {
        state.isFetching = false;
        state.isError = true;
      },
      reset: (state) => {
        state.isFetching = false;
        state.isError = false;
        state.companies = [];
        state.checkedCompanyIds = [];
        state.checkedCompaniesColors = {};
      },
      check: (state, { payload }: PayloadAction<Company['id'][]>) => {
        const usedColors = new Set(Object.values(state.checkedCompaniesColors));
        let hslIndex = 0;

        for (const companyId of payload) {
          if (!state.checkedCompanyIds.includes(companyId)) {
            state.checkedCompanyIds.push(companyId);
          }

          if (!state.checkedCompaniesColors[companyId]) {
            const color = getNextAvailableColor(usedColors, hslIndex);
            state.checkedCompaniesColors[companyId] = color;
            usedColors.add(color);
            if (color.startsWith('hsl')) hslIndex++;
          }
        }
      },
      uncheck: (state, { payload }) => {
        state.checkedCompanyIds = state.checkedCompanyIds.filter(
          (layerId) => !payload.includes(layerId)
        );
        for (const companyId of payload) {
          delete state.checkedCompaniesColors[companyId];
        }
      },
      select: (state, { payload }) => {
        state.selectedCompanyId = payload;
      },
      load: (state, { payload }: PayloadAction<Company | Company[]>) => {
        if (isArray(payload)) {
          state.companies = uniqBy([...state.companies, ...payload], 'id');
        } else {
          state.companies = uniqBy([...state.companies, payload], 'id');
        }
      },
      update: (
        state,
        {
          payload,
        }: PayloadAction<{ companyId: Company['id']; company: Company }>
      ) => {
        const companyToUpdate = findOrThrow(
          state.companies,
          ({ id }) => id === payload.companyId
        );

        Object.assign(companyToUpdate, payload.company, {
          updatedAt: new Date().toISOString(),
        });
      },
      markAsRecentlyViewed: (
        state,
        { payload }: PayloadAction<Company | Company[]>
      ) => {
        const ids = isArray(payload)
          ? payload.map(({ id }) => id)
          : [payload.id];
        state.recentlyViewedCompanyIds = uniq([
          ...ids,
          ...state.recentlyViewedCompanyIds,
        ]);
      },
      setCheckedCompanyColor: (
        state,
        {
          payload,
        }: PayloadAction<{
          companyId: Company['id'];
          color: string;
        }>
      ) => {
        state.checkedCompaniesColors[payload.companyId] = payload.color;
      },
    },
  });

export const companiesSlice = createCompaniesSlice('companies');

const muiColors = [
  '#ffa000', //yellow
  '#f44336', //red
  '#03a9f4', //light blue
  '#1B5E20', //dark green
  '#9c27b0', //purple
  '#009688', //teal
  '#1565c0', //dark blue
  '#ff5722', //orange
  '#795548', //brown
  '#607d8b', //blue grey
  '#673ab7', //deep purple
  '#e91e63', //pink
  '#4caf50', //green
  '#cddc39', //lime
  '#00bcd4', //cyan
  '#3f51b5', //indigo
  '#8bc34a', //light green
  '#2196f3', //blue
  '#ff9800', //deep orange
  '#9e9e9e', //grey
];

function getNextAvailableColor(usedColors: Set<string>, indexHint = 0): string {
  // Try unused MUI color first
  for (const color of muiColors) {
    if (!usedColors.has(color)) {
      return color;
    }
  }

  // Fallback: Generate unique HSL
  let i = indexHint;
  while (true) {
    const hue = (i * 137.508) % 360;
    const hsl = `hsl(${hue}, 75%, 50%)`;
    if (!usedColors.has(hsl)) {
      return hsl;
    }
    i++;
  }
}
